/*
 * Master View for the Single Page Application.
 * This view loads all the other components
 */
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import createEmotionServer from '@emotion/server/create-instance';
import { loadableReady } from '@loadable/component';
import { ChunkExtractor } from '@loadable/server';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import theme from '../theme';
import ConsignmentSection from '@Sections/ConsignmentSection';
import ContactSection from '@Sections/ContactSection';
import SubmissionSection from '../Sections/SubmissionSection';
import CropInformationSection from '@Sections/CropInformationSection';
import EquipmentInfoSection from '@Sections/EquipmentInfoSection';
import LivestockInformationSection from '@Sections/LivestockInformationSection';
import MediaSection from '@Sections/MediaSection';
import ServiceLogSection from '@Sections/ServiceLogSection';
import PreviewCropsSection from '@Sections/PreviewCropsSection';
import PreviewLivestockSection from '@Sections/PreviewLivestockSection';
import PreviewSection from '@Sections/PreviewSection';
import { ErrorBoundary } from '../ErrorBoundary';
import CookieConsentModal from '@Utilities/CookieConsentModal';

class MasterView extends React.Component {

    //The props is passed from the view(cshtml)
    constructor(props) {
        super(props);

        this.state = MasterView.modelToState(props);

        this.getHiddenPropertyName = this.getHiddenPropertyName.bind(this);
        this.getVisibilityEvent = this.getVisibilityEvent.bind(this);
        this.getBrowserPrefix = this.getBrowserPrefix.bind(this);
        this.handlePageClose = this.handlePageClose.bind(this);
        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleFormUpdate = this.handleFormUpdate.bind(this);
        this.handleFormUpdateForMedia = this.handleFormUpdateForMedia.bind(this);
        this.changeListingType = this.changeListingType.bind(this);
        this.submitListing = this.submitListing.bind(this);
        this.GetMLData = this.GetMLData.bind(this);
        this.updateEquipmentInfoAutosave = this.updateEquipmentInfoAutosave.bind(this);

        this.contactSectionRef = React.createRef();
        this.equipmentInfoAutosaveTimer = null;
        this.equipmentInfoAutosaveSnapshot = null;
        this.equipmentInfoAccordionObserver = null;
    }

    //set the react state to the the same values as the model
    static modelToState(props) {
        return {
            ListingDetailViewModel: props.model.ListingDetailViewModel,
            LivestockViewModel: props.model.LivestockViewModel,
            CropsViewModel: props.model.CropsViewModel,
            SpecsViewModel: props.model.SpecsViewModel,
            FitsToViewModel: props.model.FitsToViewModel,
            MediaViewModel: props.model.MediaViewModel,
            ServiceLogViewModel: props.model.ServiceLogViewModel,
            PriceViewModel: props.model.PriceViewModel,
            ConsignmentViewModel: props.model.ConsignmentViewModel,
            ContactViewModel: props.model.ContactViewModel,
            CatMakeModelValid: props.model.CatMakeModelValid,
            FeedBackEmailLink: props.model.FeedBackEmailLink,
            HelpPhoneNumber: props.model.HelpPhoneNumber,
            HomePage: props.model.HomePage,
            CurrentSite: props.model.CurrentSite,
            QueryString: props.model.QueryString,
            IsRegisteredUser: props.model.IsRegisteredUser,
            IsAttachment: props.model.IsAttachment,
            IsLivestock: props.model.IsLivestock,
            IsCrop: props.model.IsCrop,
            SaleType: props.model.SaleType,
            IsConsignment: props.model.IsConsignment,
            IncludeAttachment: props.model.IncludeAttachment,
            ExcludeFitsTo: props.model.ExcludeFitsTo,
            IsAircraft: props.model.IsAircraft,
            SecureDomainURL: props.model.SecureDomainURL,
            IsFacebookLogin: props.model.IsFacebookLogin,
            IsGoogleLogin: props.model.IsGoogleLogin,
            InvisibleReCAPTCHAPublicKey: props.model.InvisibleReCAPTCHAPublicKey,
            Translations: props.model.Translations,
            HighlightErrors: {},
            FormErrors: [],
            InputErrors: {},
            SimplifiedSpecs: props.model.SimplifiedSpecs,
            HasTranslationEditorRights: props.model.HasTranslationEditorRights,
            IsInTranslationEditorMode: props.model.IsInTranslationEditorMode,
            CurrentTrackingUserID: props.model.CurrentTrackingUserID,
            ShowCookieModal: props.model.ShowCookieModal,
            UseUserCentricsCMP: props.model.UseUserCentricsCMP,
            CookieModalProps: props.model.CookieModalProps,
            IsRearrangedDetailsPage: props.model.IsRearrangedDetailsPage,
        };
    }

    componentDidMount() {
        if (this.state.ListingDetailViewModel.SerialVIN == "")
            $('input[id="SerialVIN"]').focus();

        if (this.state.IsFacebookLogin || this.state.IsGoogleLogin) {
            OpenSpecificHeaderAccordion(ContactSectionID);
            setTimeout(function () {
                ScrollToHeader("#header-accordion-3");
            }, 500);
        }

        if (this.state.IsRearrangedDetailsPage) {
            OpenSpecificHeaderAccordion(ContactSectionID);
            setTimeout(function () {
                ScrollToHeader("#header-accordion-3");
            }, 500);
        }

        window.PageViewForCookie = this;
        //set up a mutation observer to watch for changes to the Equipment Info accordion
        this.equipmentInfoAccordionObserver = new MutationObserver(this.updateEquipmentInfoAutosave);
        this.equipmentInfoAccordionObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true
        });
        this.updateEquipmentInfoAutosave();

        var browserPrefix = this.getBrowserPrefix();
        document.addEventListener(this.getVisibilityEvent(browserPrefix), this.handlePageClose);
    }

    componentWillUnmount() {
        document.removeEventListener(this.getVisibilityEvent(browserPrefix), this.handlePageClose);
        //disconnect the obeserver when the component unmounts
        if (this.equipmentInfoAccordionObserver) {
            this.equipmentInfoAccordionObserver.disconnect();
            this.equipmentInfoAccordionObserver = null;
        }
        //Clear the autosave timer when the component unmounts
        if (this.equipmentInfoAutosaveTimer) {
            clearInterval(this.equipmentInfoAutosaveTimer);
            this.equipmentInfoAutosaveTimer = null;
        }
        this.equipmentInfoAutosaveSnapshot = null;
    }
    //returns a snap shot of the current equipment info state as a JSON string for comparison with the last saved snapshot
    getEquipmentInfoAutosaveSnapshot() {
        return JSON.stringify(this.state);
    }
    //saves the equipment info if it has changed since the last save
    saveEquipmentInfoIfChanged() {
        var currentSnapshot = this.getEquipmentInfoAutosaveSnapshot();
        if (currentSnapshot !== this.equipmentInfoAutosaveSnapshot) {
            //function to run after a successful save
            this.SaveAllSections(false, false, () => {
                //Only update the snapshot after a successful save
                    this.equipmentInfoAutosaveSnapshot = currentSnapshot;
            });
        }
    }
    //checks if the Equipment Info accordion is open or closed and starts/stops the autosave timer accordingly
    updateEquipmentInfoAutosave() {
        var equipmentInfoIsOpen = $('#header-accordion-' + EnterInfoSectionID).hasClass('expanded');
        //Start the autosave timer if the Equipment Info accordion is open and the timer is not already running
        if (equipmentInfoIsOpen && !this.equipmentInfoAutosaveTimer) {
            this.equipmentInfoAutosaveSnapshot = this.getEquipmentInfoAutosaveSnapshot();
            this.equipmentInfoAutosaveTimer = setInterval(() => {
                this.saveEquipmentInfoIfChanged();
            }, 300000); //5 minutes in milliseconds
        }
        //Stop the autosave timer if the Equipment Info accordion is closed and the timer is running
        else if (!equipmentInfoIsOpen && this.equipmentInfoAutosaveTimer) {
            clearInterval(this.equipmentInfoAutosaveTimer);
            this.equipmentInfoAutosaveTimer = null;
            this.equipmentInfoAutosaveSnapshot = null;
        }
    }

    getHiddenPropertyName(prefix) {
        return (prefix ? prefix + 'Hidden' : 'hidden');
    }

    getVisibilityEvent(prefix) {
        return (prefix ? prefix : '') + 'visibilitychange';
    }

    getBrowserPrefix() {
        var browserPrefixes = ['moz', 'ms', 'o', 'webkit'];
        for (var i = 0; i < browserPrefixes.length; i++) {
            if (this.getHiddenPropertyName(browserPrefixes[i]) in document) {
                return browserPrefixes[i];
            }
        }
        return null;
    }

    handlePageClose() {
        var browserPrefix = this.getBrowserPrefix();
        if (document[this.getHiddenPropertyName(browserPrefix)] || (document.visibilityState === 'unloaded' || document.visibilityState === 'hidden')) {
            this.SaveAllSections(true);
        }
    }

    handleButtonClick(button) { //executes a command when you click on a button
        switch (button) {
            case "cancel":
                window.location = '/';
                break;
            case "fwd":
                if (!this.state.IsInTranslationEditorMode) {
                    Spinner.Show($('#pagebody'), true);

                    var xhr = new XMLHttpRequest();
                    xhr.onload = function () {
                        var response = JSON.parse(xhr.response);
                        window.location = response.oLandingPageViewModel.NextPageURL;
                    }.bind(this);

                    xhr.open("POST", '/listinginput/landingpage/next', true);
                    xhr.setRequestHeader("Content-type", "application/json");
                    xhr.send(JSON.stringify(this.state));
                }
                break;
        }
    }

    handleFieldChanged(field, value) {
        this.setState({
            [field]: value
        }, () => {
            //if adding to this make sure the save doesnt overwrite any values with bad data.
            var objectToSend = {
                SaleType: this.state.SaleType,
            }

            var xhr = new XMLHttpRequest();
            xhr.open("POST", '/listinginput/details/SaveMasterViewModelObjectXML' + this.state.QueryString, true);
            xhr.setRequestHeader("Content-type", "application/json");
            xhr.send(JSON.stringify(objectToSend));
        });
    }

    //update this method if a new field is added to the page
    SaveAllSections(pageClose, synchronous, onSuccess) {
        var state = this.state;
        var objectToSend = {};

        //consignment
        if (state.IsConsignment) {
            var CRMIDSToSave = [];
            for (let key in this.state.ConsignmentViewModel.Auctioneers) {
                if (this.state.ConsignmentViewModel.AuctioneerCheckBoxHandler[key]) {
                    CRMIDSToSave.push(key.toString());
                }
            }

            //async save fields 
            var ConsignmentViewModel = {
                CRMIDSToSave: CRMIDSToSave,
                PreferredSaleDateSelected: state.ConsignmentViewModel.PreferredSaleDateSelected,
            };

            objectToSend = {
                ...objectToSend,
                ConsignmentViewModel: ConsignmentViewModel,
            };
        }

        //crop
        if (state.IsCrop) {
            var CropsViewModel = {
                BaleTypeCatID: state.CropsViewModel.BaleTypeCatID,
                NumberOfBales: state.CropsViewModel.NumberOfBales,
                NumberOfTons: state.CropsViewModel.NumberOfTons,
                PricePerBale: state.CropsViewModel.PricePerBale,
                PricePerTon: state.CropsViewModel.PricePerTon,
                CurrencyCodeSelected: state.CropsViewModel.CurrencyCodeSelected,
                Description: state.CropsViewModel.Description,
                PriceDescription: state.CropsViewModel.PriceDescription
            };

            var SpecsViewModel = {
                SpecPage: state.SpecsViewModel.currentPage,
                FormInputs: state.SpecsViewModel.FormInputs,
                Category: state.CropsViewModel.BaleTypeCatID
            };

            objectToSend = {
                ...objectToSend,
                CropsViewModel: CropsViewModel,
                SpecsViewModel: SpecsViewModel,
            };
        }
        else if (this.state.IsLivestock) {
            var LiveStockViewModel = {
                AnimalCategoryID: state.LivestockViewModel.AnimalCategoryID,
                TypeCategoryID: state.LivestockViewModel.TypeCategoryID,
                ClassCategoryID: state.LivestockViewModel.ClassCategoryID,
                Description: state.LivestockViewModel.Description,
                PriceInfoLivestockViewModel: state.LivestockViewModel.PriceInfoLivestockViewModel,

                QueryString: this.state.QueryString,
            };

            var SpecsViewModel = {
                SpecPage: state.SpecsViewModel.currentPage,
                FormInputs: state.SpecsViewModel.FormInputs,
                Category: state.LivestockViewModel.ClassCategoryID,
            };

            objectToSend = {
                ...objectToSend,
                LiveStockViewModel: LiveStockViewModel,
                SpecsViewModel: SpecsViewModel,
                Description: state.ListingDetailViewModel.Description,
            };
        }
        else {
            var ListingDetailViewModel = {
                SystemOfMeasurement: state.ListingDetailViewModel.SystemOfMeasurement,
                ManufacturerSelected: state.ListingDetailViewModel.ManufacturerSelected,
                ModelSelected: state.ListingDetailViewModel.ModelSelected,
                SerialVIN: state.ListingDetailViewModel.SerialVIN,
                YearSelected: state.ListingDetailViewModel.YearSelected,
                EquipmentIdentifier: state.ListingDetailViewModel.EquipmentIdentifier,
                StockNumber: state.ListingDetailViewModel.StockNumber,
                Condition: state.ListingDetailViewModel.Condition,
                Description: state.ListingDetailViewModel.Description,
                Quantity: state.ListingDetailViewModel.Quantity,
                Weight: state.ListingDetailViewModel.Weight,
                CategorySelected: state.ListingDetailViewModel.CategorySelected,
                BaseCatID: state.ListingDetailViewModel.BaseCatID
            };

            if (!state.IsAttachment) {
                var SpecsViewModel = {
                    SpecPage: state.SpecsViewModel.currentPage,
                    FormInputs: state.SpecsViewModel.FormInputs,
                    SerialVIN: state.ListingDetailViewModel.SerialVIN,
                    Category: state.SpecsViewModel.Category,
                    Manufacturer: state.ListingDetailViewModel.ManufacturerSelected,
                    Model: state.ListingDetailViewModel.ModelSelected,
                    Year: state.ListingDetailViewModel.YearSelected,
                };
            }

            var PriceViewModel = {
                CurrencyCodeSelected: state.PriceViewModel.CurrencyCodeSelected,
                Price: state.PriceViewModel.Price,
                FEClicks: state.PriceViewModel.FEClicks,
                ExcludeGoodsAndServicesTax: state.PriceViewModel.ExcludeGoodsAndServicesTax
            };

            if (state.CurrentSite == "Controller" ||
                state.CurrentSite == "Aviation Trader" ||
                state.CurrentSite == "Controller EMEA") {
                PriceViewModel.ForSale = state.PriceViewModel.ForSale;
                PriceViewModel.LeasePrice = state.PriceViewModel.LeasePrice;
                PriceViewModel.ForLease = state.PriceViewModel.ForLease;
                PriceViewModel.FractionalPrice = state.PriceViewModel.FractionalPrice;
                PriceViewModel.FractionalPercent = state.PriceViewModel.FractionalPercent;
                PriceViewModel.ForFractional = state.PriceViewModel.ForFractional;
            }

            if (state.CurrentSite == "RentalYard") {
                PriceViewModel.ForRent = true;
                PriceViewModel.DailyRentalPrice = state.PriceViewModel.DailyRentalPrice;
                PriceViewModel.WeeklyRentalPrice = state.PriceViewModel.WeeklyRentalPrice;
                PriceViewModel.MonthlyRentalPrice = state.PriceViewModel.MonthlyRentalPrice;
            }

            objectToSend = {
                ...objectToSend,
                ListingDetailViewModel: ListingDetailViewModel,
                SpecsViewModel: SpecsViewModel,
                PriceViewModel: PriceViewModel,
                EmailVerified: state.ContactViewModel.EmailVerified,
                IsAttachment: state.IsAttachment
            };
        }

        //delete some properties not needed to save to xml that cause issues when parsing on the backend
        var locationViewModel = state.ContactViewModel.LocationViewModel;
        delete locationViewModel.Countries;
        delete locationViewModel.AllStates;
        delete locationViewModel.States;

        var contactViewModel = {
            Email: state.ContactViewModel.Email,
            bReceiveEmails: state.ContactViewModel.bReceiveEmails,
            bPrivacyPolicyChecked: state.ContactViewModel.bPrivacyPolicyChecked,
            Company: state.ContactViewModel.Company,
            Phone: state.ContactViewModel.Phone,
            EmailIsRegistered: state.ContactViewModel.EmailIsRegistered,
            FirstName: state.ContactViewModel.FirstName,
            LastName: state.ContactViewModel.LastName,
            ReceiveEmails: state.ContactViewModel.ReceiveEmails,
            ClickedForgotPassword: state.ContactViewModel.ClickedForgotPassword,
            LocationViewModel: locationViewModel,
            UnfinishedLeadNewPartialCRMID: state.ContactViewModel.UnfinishedLeadNewPartialCRMID
        };

        objectToSend = {
            ...objectToSend,
            IsLivestock: state.IsLivestock,
            IsCrop: state.IsCrop,
            IsAircraft: state.IsAircraft,
            IsConsignment: state.IsConsignment,
            IsAttachment: state.IsAttachment,
            //contact
            ContactViewModel: contactViewModel,
            //images saved separately
            QueryString: this.state.QueryString,
        };

        var xhr = new XMLHttpRequest();
        //xhr.onload is ran after the request is completed, regardless of success or failure
        xhr.onload = function () {
            SaveMediaInTheModal(true, true);
            //if the status was successful, call the onSuccess callback to update the snapshot after a successful save
            if (xhr.status >= 200 && xhr.status < 300) {                
                //call the onSuccess callback to update the snapshot after a successful save
                if (onSuccess) onSuccess();
            }
        }.bind(this);

        if (pageClose && pageClose == true) {
            //send data in a way that always succeeds, even when page closes
            var blob = new Blob([JSON.stringify(objectToSend)], { type: 'text/plain; charset=UTF-8' });
            navigator.sendBeacon("/listinginput/details/SaveAllSections" + this.state.QueryString, blob);
        }
        else {
            xhr.open("POST", '/listinginput/details/SaveAllSections' + this.state.QueryString, !(synchronous && synchronous == true));
            xhr.setRequestHeader("Content-type", "application/json");
            xhr.send(JSON.stringify(objectToSend));
        }
    }

    handleFormUpdate(Model) {   //updates the state in View with new values when they change. 
        this.setState({
            ListingDetailViewModel: Model.ListingDetailViewModel || this.state.ListingDetailViewModel,
            validatorListingDetail: Model.validatorListingDetail || this.state.validatorListingDetail,
            SpecsViewModel: Model.SpecsViewModel || this.state.SpecsViewModel,
            ValidateAndSaveSpecs: Model.ValidateAndSaveSpecs || this.state.ValidateAndSaveSpecs,
            ValidateSpec: Model.ValidateSpec || this.state.ValidateSpec,
            SaveSpecsToXML: Model.SaveSpecsToXML || this.state.SaveSpecsToXML,
            FitsToViewModel: Model.FitsToViewModel || this.state.FitsToViewModel,
            validatorFitsTo: Model.validatorFitsTo || this.state.validatorFitsTo,
            MediaViewModel: Model.MediaViewModel || this.state.MediaViewModel,
            ServiceLogViewModel: Model.ServiceLogViewModel || this.state.ServiceLogViewModel,
            validatorMedia: Model.validatorMedia || this.state.validatorMedia,
            PriceViewModel: Model.PriceViewModel || this.state.PriceViewModel,
            validatorPrice: Model.validatorPrice || this.state.validatorPrice,
            ContactViewModel: Model.ContactViewModel || this.state.ContactViewModel,
            validatorContact: Model.validatorContact || this.state.validatorContact,
            ConsignmentViewModel: Model.ConsignmentViewModel || this.state.ConsignmentViewModel,
            validatorConsignment: Model.validatorConsignment || this.state.validatorConsignment,
            validatorEquipmentInfo: Model.validatorEquipmentInfo || this.state.validatorEquipmentInfo,
            LivestockViewModel: Model.LivestockViewModel || this.state.LivestockViewModel,
            CropsViewModel: Model.CropsViewModel || this.state.CropsViewModel,
            validatorLivestockInfo: Model.validateLivestockInfo || this.state.validateLivestockInfo,
            validatorCropInfo: Model.validatorCropInfo || this.state.validatorCropInfo,
            SaveExitMethod: Model.SaveExitMethod || this.state.SaveExitMethod,
            HighlightErrors: Model.HighlightErrors,
            FormErrors: Model.FormErrors,
            InputErrors: Model.InputErrors,
            CurrentTrackingUserID: Model.CurrentTrackingUserID || this.state.CurrentTrackingUserID,
            ShowCookieModal: Model.ShowCookieModal || this.state.ShowCookieModal,
            IsRearrangedLandingPage: Model.IsRearrangedLandingPage || this.state.IsRearrangedLandingPage,
            IsRearrangedDetailsPage: Model.IsRearrangedDetailsPage || this.state.IsRearrangedDetailsPage
        });
    }

    handleFormUpdateForMedia(Model) {
        this.setState({
            ...this.state,
            MediaViewModel: Model.MediaViewModel || this.state.MediaViewModel,
            ShowCookieModal: Model.ShowCookieModal || this.state.ShowCookieModal,
            validatorMedia: Model.validatorMedia || this.state.validatorMedia
        });
    }

    handleFormUpdateForServiceLogs(Model) {
        this.setState({
            ...this.state,
            ServiceLogViewModel: Model.ServiceLogViewModel || this.state.ServiceLogViewModel,
            ShowCookieModal: Model.ShowCookieModal || this.state.ShowCookieModal
        });
    }

    //Methods
    submitListing() {
        var state = this.state;

        //logged in users, or verified emails do not need recaptcha
        if (state.ContactViewModel.IsRegisteredUser || state.ContactViewModel.EmailVerified) {
            //submit listing
            var email = "";
            if (state.ContactViewModel.VerifyUserName)
                email = state.ContactViewModel.Username;
            else
                email = state.ContactViewModel.Email

            var ContactViewModel = {
                Email: email,
                CRMID: state.ContactViewModel.CRMID,
                Password: state.ContactViewModel.Password,
                ReceiveEmails: state.ContactViewModel.ReceiveEmails,
                Phone: state.ContactViewModel.Phone,
            };

            var PriceViewModel = {
                ExcludeGoodsAndServicesTax: state.PriceViewModel.ExcludeGoodsAndServicesTax
            };

            var objectToSend = {
                ContactViewModel: ContactViewModel,
                PriceViewModel: PriceViewModel,
                QueryString: state.QueryString,
            };

            var xhr = new XMLHttpRequest();
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    var response = JSON.parse(xhr.response);

                    if (!dataLayer_ContainsEvent(gTagEvents.addListingSuccess)) {
                        window.dataLayer.push({
                            "event": gTagEvents.addListingSuccess
                        });
                    }
                    //only save the consignor event (for logged in users) if these properties have values
                    if (state.ConsignmentViewModel.SelectedConsignorName && state.ConsignmentViewModel.SelectedConsignorCRMID) {
                        if (!dataLayer_ContainsEvent(gTagEvents.featuredAuctioneerConsignmentComplete)) {
                            window.dataLayer.push({
                                'event': gTagEvents.featuredAuctioneerConsignmentComplete,
                                'featuredAuctioneerConsignmentName': state.ConsignmentViewModel.SelectedConsignorName,
                                'featuredAuctioneerConsignmentCRMID': state.ConsignmentViewModel.SelectedConsignorCRMID
                            });
                        }
                    }
                    window.location = response.sNextPageURL;
                }
            }.bind(this);

            xhr.open("POST", '/listinginput/details/SubmitListing' + state.QueryString, true);
            xhr.setRequestHeader("Content-type", "application/json");
            xhr.send(JSON.stringify(objectToSend));
        }
        else {
            //store info so recaptcha can use after verificaton
            if (state.ContactViewModel.VerifyUserName)
                $(".hidden-reCAPTCHA-Email").text(state.ContactViewModel.Username);
            else
                $(".hidden-reCAPTCHA-Email").text(state.ContactViewModel.Email);

            $(".hidden-reCAPTCHA-CRMID").text(state.ContactViewModel.CRMID);
            $(".hidden-reCAPTCHA-UnfinishedLeadNewPartialCRMID").text(state.ContactViewModel.UnfinishedLeadNewPartialCRMID);
            $(".hidden-reCAPTCHA-Password").text(state.ContactViewModel.Password);
            $(".hidden-reCAPTCHA-ReceiveEmails").text(state.ContactViewModel.ReceiveEmails);
            $(".hidden-reCAPTCHA-QueryString").text(state.QueryString);
            $(".hidden-reCAPTCHA-ExcludeGoodsAndServicesTax").text(state.PriceViewModel.ExcludeGoodsAndServicesTax);

            grecaptcha.execute();   //will call verifyReCAPTCHA in SinglePageUI.js when it is done

            document.addEventListener('click', function () {
                target = $("iframe[title='recaptcha challenge']");
                flag = event.path.some(function (el, i, arr) {
                    return (el == target);
                });
                if (!flag) {
                    Spinner.Remove();
                }
            });
        }
    }

    changeListingType(field, bIsAttachment) {
        if (!this.state.IsInTranslationEditorMode) {
            Spinner.Show($('#pagebody'), true);

            var state = this.state;

            switch (field) {
                case "attachment":
                    var objectToSend = {
                        QueryString: state.QueryString,
                        IsAttachment: bIsAttachment,
                    };

                    //synchronous call to change the listing type
                    var xhr = new XMLHttpRequest();
                    xhr.onload = function () {
                        var response = JSON.parse(xhr.response);
                        Spinner.Remove();
                        state = response.oMasterViewModel;

                        state.IsAttachment = bIsAttachment;

                        this.handleFormUpdate(state);
                        this.setState({
                            IsAttachment: state.IsAttachment,
                            Translations: state.Translations
                        });
                    }.bind(this);

                    xhr.open("POST", '/listinginput/details/changelistingtype' + this.state.QueryString, true);
                    xhr.setRequestHeader("Content-type", "application/json");
                    xhr.send(JSON.stringify(objectToSend));
                    break;
                case "livestockCrop":
                    var objectToSend = {
                        QueryString: state.QueryString,
                        IsLivestock: !state.IsLivestock,
                        IsCrop: !state.IsCrop,
                    };

                    //synchronous call to change the listing type
                    var xhr = new XMLHttpRequest();
                    xhr.onload = function () {
                        var response = JSON.parse(xhr.response);
                        Spinner.Remove();
                        state = response.oMasterViewModel;

                        state.IsAttachment = bIsAttachment;

                        this.handleFormUpdate(state);
                        this.setState({
                            IsLivestock: state.IsLivestock,
                            IsCrop: state.IsCrop,
                            Translations: state.Translations
                        });
                    }.bind(this);

                    xhr.open("POST", '/listinginput/details/ToggleLivestockHayStraw' + this.state.QueryString, true);
                    xhr.setRequestHeader("Content-type", "application/json");
                    xhr.send(JSON.stringify(objectToSend));
                    break;
            }
        }
    }

    SaveAndExit() {
        //this calls the javascript close in the SinglePageUI.js
        CloseModal();

        var state = this.state;

        this.SaveAllSections();

        if (state.IsRegisteredUser == true) {
            window.location = this.state.HomePage;
        }
        else {
            var objectToSend = {
                Email: state.ContactViewModel.Email,
                Password: state.ContactViewModel.Password,
            };

            var xhr = new XMLHttpRequest();
            xhr.onload = function () {
                var response = JSON.parse(xhr.response)
                // Go back to the home while logged in
                window.location = response.oContactModel.NextPageURL;
            }.bind(this);

            xhr.open("POST", '/listinginput/registration/VerifyEmailPasswordCombo' + this.state.QueryString, false);
            xhr.setRequestHeader("Content-type", "application/json");
            xhr.send(JSON.stringify(objectToSend));
        }
    }

    GetMLData() {
        // called when EquipmentInfo accordion is expanded
        var lstKeys = [];
        for (var key in this.state.MediaViewModel.MachineLearningPhotoData) {
            if (document.getElementById(key) !== null) { //only use ML data for photos that have been uploaded and also not deleted
                lstKeys.push(key);
            }
        }

        if (lstKeys.length == 0) {
            return this.state.ListingDetailViewModel;
        }

        var objectToSend = {
            lstMediaIDs: lstKeys,
            isAttachment: this.state.IsAttachment
        };

        //var sDescription = this.state.ListingDetailViewModel.Description ?? "";
        var state = this.state;

        var xhr = new XMLHttpRequest();
        xhr.onload = function () {
            var response;
            if (xhr.status == 200) {
                response = JSON.parse(xhr.response);
            }
            else {
                return;
            }

            if (response.result && !response.error) { //set necessary fields
                state.ListingDetailViewModel.MLAutofilled = false;
                state.ListingDetailViewModel.AutofilledFields = [];
                //if (sDescription != "") {
                //    state.ListingDetailViewModel.Description = sDescription;
                //    state.ListingDetailViewModel.MLAutofilled = true;
                //}
                if (response.result.SerialVIN && response.result.ResolvedDecodingType && IsNullOrWhiteSpace(state.ListingDetailViewModel.SerialVIN)) {
                    state.ListingDetailViewModel.SerialVIN = response.result.SerialVIN;
                    state.ListingDetailViewModel.MLAutofilled = true;
                    state.ListingDetailViewModel.AutofilledFields.push("SerialVIN");
                }
                else if (this.state.ListingDetailViewModel.AutofilledFields.includes("SerialVIN")) {
                    state.ListingDetailViewModel.SerialVIN = "";
                }
                if (response.result.Manufacturer && IsNullOrWhiteSpace(state.ListingDetailViewModel.ManufacturerSelected)) {
                    state.ListingDetailViewModel.ManufacturerSelected = response.result.Manufacturer;
                    state.ListingDetailViewModel.MLAutofilled = true;
                    state.ListingDetailViewModel.AutofilledFields.push("Manufacturer");
                }
                else if (this.state.ListingDetailViewModel.AutofilledFields.includes("Manufacturer")) {
                    state.ListingDetailViewModel.ManufacturerSelected = "";
                }
                if (response.result.Model && IsNullOrWhiteSpace(state.ListingDetailViewModel.ModelSelected)) {
                    state.ListingDetailViewModel.ModelSelected = response.result.Model;
                    state.ListingDetailViewModel.MLAutofilled = true;
                    state.ListingDetailViewModel.AutofilledFields.push("Model");
                }
                else if (this.state.ListingDetailViewModel.AutofilledFields.includes("Model")) {
                    state.ListingDetailViewModel.ModelSelected = "";
                }
                if (this.state.CurrentSite != "RVUniverse" && response.result.CategoryID && (IsNullOrWhiteSpace(state.ListingDetailViewModel.CategorySelected) || state.ListingDetailViewModel.CategorySelected == 0)) {
                    var CatsList = state.ListingDetailViewModel.AllCategoriesList || state.ListingDetailViewModel.CategoriesList;
                    var bCatsUnavailable = !(CatsList) || CatsList.length == 0;
                    var CatsFiltered = [];
                    if (!bCatsUnavailable) {
                        CatsFiltered = CatsList.filter(x => x.Value === String(response.result.CategoryID));
                    }
                    if ((CatsFiltered && CatsFiltered.length > 0)) { //don't autofill category if it is not available on the site
                        state.ListingDetailViewModel.CategorySelected = String(response.result.CategoryID);
                        state.ListingDetailViewModel.MLAutofilled = true;
                        state.ListingDetailViewModel.AutofilledFields.push("Category");
                    }
                }
                else if (this.state.ListingDetailViewModel.AutofilledFields.includes("Category")) {
                    state.ListingDetailViewModel.CategorySelected = "";
                }
                if (response.result.BaseCategoryID) {
                    //only set the base cat if a category was returned from ML and it was used
                    if (response.result.CategoryID && state.ListingDetailViewModel.CategorySelected == String(response.result.CategoryID)) {
                        state.ListingDetailViewModel.InventoryType = response.InventoryType;
                        state.ListingDetailViewModel.BaseCatID = response.result.BaseCategoryID;
                    }
                }
                else if (this.state.CurrentSite == "AuctionTime" || this.state.CurrentSite == "AuctionValues") {
                    state.ListingDetailViewModel.InventoryType = 4;
                    state.ListingDetailViewModel.BaseCatID = 4;
                }
                else {
                    state.ListingDetailViewModel.InventoryType = 0;
                    state.ListingDetailViewModel.BaseCatID = 0;
                }
                if (response.result.Year > 0 && IsNullOrWhiteSpace(state.ListingDetailViewModel.YearSelected)) {
                    state.ListingDetailViewModel.YearSelected = response.result.Year;
                    state.ListingDetailViewModel.MLAutofilled = true;
                    state.ListingDetailViewModel.AutofilledFields.push("Year");
                }
                else if (this.state.ListingDetailViewModel.AutofilledFields.includes("Year")) {
                    state.ListingDetailViewModel.YearSelected = "";
                }

                if (state.ListingDetailViewModel.MLAutofilled == true) {
                    this.handleFormUpdate(state);
                }
            }
        }.bind(this);

        xhr.open("POST", `/listinginput/ajax/GetMachineLearningData`, false);
        xhr.setRequestHeader("Content-type", "application/json");
        xhr.send(JSON.stringify(objectToSend));
    }

    getTranslation(key) {
        return this.state.IsInTranslationEditorMode ?
            <span dangerouslySetInnerHTML={{ __html: this.state.Translations[key] }}></span> :
            this.state.Translations[key];
    }

    //the html and react components to render
    render() {

        var showAttachment = this.state.IncludeAttachment;
        var showLivestockHayStrawSwitcher = false;

        const cookieModalProps = this.state.CookieModalProps || {};

        if (this.state.CurrentSite == "LivestockMarket" || this.state.CurrentSite == "EquineMarket") {
            showAttachment = false;
            showLivestockHayStrawSwitcher = true;
        }

        var contactOrder = "3."; //if this is a consignment, contact is the 4th section

        var consignmentSection = [];
        if (this.state.IsConsignment) {
            contactOrder = "4.";
            if (this.state.IsRearrangedDetailsPage) {
                contactOrder = "2."; // Rearranged site has different order
            }
            consignmentSection.push(
                <div className="expand" key="consignmentAccordian">
                    <button role="link" onClick={(e) => {
                        var selection = ConsignmentHeaderAccordion(ConsignmentSectionID, this.state.ConsignmentViewModel, this.state.ContactViewModel.CRMID > 0 && !this.state.hasBeenOpened);
                        if (this.state.ConsignmentViewModel && selection) {
                            this.setState({
                                ...this.state,
                                ConsignmentViewModel: { ...this.state.ConsignmentViewModel, AuctioneerCheckBoxHandler: selection.auctioneerCheckBoxHandler },
                                //only want to update the map once based of CRM location
                                hasBeenOpened: true
                            });
                        }
                        this.SaveAllSections();
                    }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-5"><strong>3. {this.getTranslation("ConsignmentHeader")}</strong></button>
                </div>
            );

            consignmentSection.push(
                <ConsignmentSection
                    key="consignmentSection"
                    masterViewModel={this.state}
                    model={this.state.ConsignmentViewModel}
                    handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                />
            );
        }

        var WhatAreYouSelling = [];
        if (showAttachment) {
            var EquipmentStyle = !this.state.IsAttachment ? { marginRight: '0px', background: '#3e3e3f', color: '#fff', border: '1px #123a40 solid' } : { marginRight: '0px' };
            var AttachmentStyle = this.state.IsAttachment ? { background: '#3e3e3f', color: '#fff', border: '1px #123a40 solid' } : {};
            WhatAreYouSelling.push(
                <div key="radioAttachment">
                    <h1>{this.getTranslation("WhatAreYouSelling")}</h1>
                    <div className="box-select">
                        <fieldset style={{ border: '0', marginLeft: '0px', paddingLeft: '0px' }}>
                            <legend style={{ display: "none" }}>{this.getTranslation("WhatAreYouSelling")}</legend>
                            <div>
                                <label htmlFor="isEquipment" style={EquipmentStyle} className="label">
                                    <input type="radio" className="radio" id="isEquipment" onChange={(e) => { this.changeListingType("attachment", false); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.changeListingType("attachment", false); } }} checked={!this.state.IsAttachment} />
                                    <strong>{this.getTranslation("EquipmentToggle")}</strong>
                                </label>
                            </div>
                            <div>
                                <label htmlFor="isAttachment" style={AttachmentStyle} className="label">
                                    <input type="radio" className="radio" id="isAttachment" onChange={(e) => { this.changeListingType("attachment", true); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.changeListingType("attachment", true); } }} checked={this.state.IsAttachment} />
                                    <strong>{this.getTranslation("AttachmentToggle")}</strong>
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </div>
            );
        }
        else if (showLivestockHayStrawSwitcher) {
            var labelText = "Livestock";
            if (this.state.CurrentSite == "EquineMarket") {
                labelText = "Horse";
            }
            var LivestockStyle = this.state.IsLivestock ? { marginRight: '0px', background: '#725146', color: '#fff', border: '1px #522e1f solid' } : { marginRight: '0px', background: '#555555', color: '#fff', border: 'none' };
            var CropStyle = this.state.IsCrop ? { background: '#725146', color: '#fff', border: '1px #522e1f solid' } : { background: '#555555', color: '#fff', border: 'none' };
            WhatAreYouSelling.push(
                <div key="radioLivestockCrop">
                    <h1 style={{ marginLeft: '15px' }}>{this.getTranslation("WhatAreYouSelling")}</h1>
                    <div className="box-select">
                        <fieldset style={{ border: '0', marginLeft: '0px', paddingLeft: '0px' }}>
                            <legend style={{ display: "none" }}>{this.getTranslation("WhatAreYouSelling")}</legend>
                            <div>
                                <label htmlFor="isLivestock" style={LivestockStyle} className="label">
                                    <input type="radio" className="radio" id="isLivestock" onChange={(e) => { this.changeListingType("livestockCrop", false); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.changeListingType("livestockCrop", false); } }} checked={this.state.IsLivestock} />
                                    <strong>{labelText}</strong>
                                </label>
                            </div>
                            <div>
                                <label htmlFor="isCrop" style={CropStyle} className="label">
                                    <input type="radio" className="radio" id="isCrop" onChange={(e) => { this.changeListingType("livestockCrop", true); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.changeListingType("livestockCrop", true); } }} checked={this.state.IsCrop} />
                                    <strong>Hay / Straw</strong>
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </div>
            );

            //Market as selector for LSM
            var PrivateTreatyStyle = this.state.SaleType == 'Private Treaty' ? { marginRight: '0px', background: '#725146', color: '#fff', border: '1px #522e1f solid' } : { marginRight: '0px', background: '#555555', color: '#fff', border: 'none' };
            var OnlineAuctionStyle = this.state.SaleType == 'Online Auction' ? { background: '#725146', color: '#fff', border: '1px #522e1f solid' } : { background: '#555555', color: '#fff', border: 'none' };
            WhatAreYouSelling.push(
                <div key="radioMarketAs">
                    <h2 style={{ marginLeft: '15px', fontSize: '20px', display: 'inline-block' }}>Market as</h2>
                    <div className="box-select">
                        <fieldset style={{ border: '0', marginLeft: '0px', paddingLeft: '0px' }}>
                            <legend style={{ display: "none" }}>{this.getTranslation("WhatAreYouSelling")}</legend>
                            <div>
                                <label htmlFor="marketAsPT" style={PrivateTreatyStyle} className="label">
                                    <input type="radio" className="radio" id="marketAsPT" value="Private Treaty" onChange={(e) => { this.handleFieldChanged("SaleType", e.target.value); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.handleFieldChanged("SaleType", e.target.value); } }} checked={this.state.SaleType == 'Private Treaty'} />
                                    <strong>Private Treaty</strong>
                                </label>
                            </div>
                            <div>
                                <label htmlFor="marketAsOA" style={OnlineAuctionStyle} className="label">
                                    <input type="radio" className="radio" id="marketAsOA" value="Online Auction" onChange={(e) => { this.handleFieldChanged("SaleType", e.target.value); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.handleFieldChanged("SaleType", e.target.value); } }} checked={this.state.SaleType == 'Online Auction'} />
                                    <strong>Online Auction</strong>
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </div>
            );
        }
        else {
            WhatAreYouSelling.push(
                <div key="noRadioHeading">
                    <h1 style={{ margin: '10px 0px 10px 15px' }}>{this.state.CurrentSite == "Aircraft" ? this.getTranslation("UploadAndShareYourAicraftPhotos") : this.getTranslation("WhatAreYouSelling")}</h1>
                </div>
            );
        }

        //Determine which Info section we'll display depending on the Site and type of listing(Equipment/Livestock/HayStraw)
        var InfoSection = [];
        var logSection = [];
        var previewSectionHolder = [];
        var infoHeader = "";
        var serviceLogHeader = "";
        if (this.state.IsLivestock) {
            infoHeader = "Enter Livestock Information";
            if (this.state.CurrentSite == "EquineMarket") {
                infoHeader = "Enter Horse Information";
            }
            InfoSection.push(
                <LivestockInformationSection
                    key="livestockInformationSection"
                    model={this.state}
                    handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                />
            );
            previewSectionHolder.push(
                <PreviewLivestockSection
                    key="previewLivestockSection"
                    model={this.state}
                />
            );
        }
        else if (this.state.IsCrop) {
            infoHeader = "Enter Hay/Straw Information";
            InfoSection.push(
                <CropInformationSection
                    key="cropInformationSection"
                    model={this.state}
                    handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                />
            );
            previewSectionHolder.push(
                <PreviewCropsSection
                    key="previewCropsSection"
                    model={this.state}
                />
            );
        }
        else {
            infoHeader = this.getTranslation("EnterVehicleHeader");
            InfoSection.push(
                <EquipmentInfoSection
                    key="equipmentInfoSection"
                    model={this.state}
                    handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                    changeListingType={(type, bIsAttachment) => { this.changeListingType(type, bIsAttachment); }}
                />
            );
            previewSectionHolder.push(
                <PreviewSection
                    key="previewSection"
                    model={this.state}
                />
            );
        }
        let mediaOrder = "1.";
        if (this.state.IsRearrangedDetailsPage) {
            mediaOrder = "3."; // Rearranged site has different order
            if (this.state.IsConsignment) {
                mediaOrder = "4."; // Consignment listing on rearranged site has different order
            }
            if (this.state.CurrentSite === "MarketBook") {
                mediaOrder = "2.";
            }
        }

        let submissionOrder = "4.";
        if (this.state.IsConsignment) {
            submissionOrder = "5.";
        }

        if ((this.state.CurrentSite == "Controller" || this.state.CurrentSite == "Aviation Trader" || this.state.CurrentSite == "Controller EMEA") &&
            !this.state.IsAttachment) {
            contactOrder = "4.";
            submissionOrder = "5.";
            mediaOrder = "4.";
            serviceLogHeader = this.getTranslation("ServiceLogHeader"); //ServiceLogHeader
            logSection.push(
                <React.Fragment key="serviceLogSection">
                    <div className="expand">
                        <button role="link" onClick={(e) => { ToggleHeaderAccordion(ServiceLogSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-6"><strong>3. {serviceLogHeader}</strong></button>
                    </div>
                    <ServiceLogSection
                        masterViewModel={this.state}
                        model={this.state.ServiceLogViewModel}
                        handleFormUpdateForServiceLogs={(e) => { this.handleFormUpdateForServiceLogs(e); }}
                    />
                </React.Fragment>
            );
        }

        let translationEditorToggle = [];
        if (this.state.HasTranslationEditorRights) {
            translationEditorToggle.push(<TranslationToggle key="TranslationToggle" />);
        }

        if (this.state.IsRearrangedDetailsPage) {
            contactOrder = "1."; // Rearranged site has different order
        }

        let mediaEquipmentSection = [];
        if (this.state.CurrentSite === "MarketBook") {
            mediaEquipmentSection.push(
                <div className="expand">
                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(MediaSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-1"><strong>{mediaOrder} {this.getTranslation("MediaHeader")}</strong></button>
                </div>
            )
            mediaEquipmentSection.push(
                <MediaSection
                    masterViewModel={this.state}
                    model={this.state.MediaViewModel}
                    handleFormUpdateForMedia={(e) => { this.handleFormUpdateForMedia(e); }}
                    GetMLData={this.GetMLData}
                />
            )
            mediaEquipmentSection.push(
                <div className="expand">
                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(EnterInfoSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-2"><strong>3. {infoHeader}</strong></button>
                </div>
            )
            mediaEquipmentSection.push(
                <div>
                    {InfoSection}

                    {logSection}
                </div>
            )
        } else {
            mediaEquipmentSection.push(
                <div className="expand">
                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(EnterInfoSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-2"><strong>2. {infoHeader}</strong></button>
                </div>
            )
            mediaEquipmentSection.push(
                <div>
                    {InfoSection}

                    {logSection}
                </div>
            )
            mediaEquipmentSection.push(
                <div className="expand">
                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(MediaSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-1"><strong>{mediaOrder} {this.getTranslation("MediaHeader")}</strong></button>
                </div>
            )
            mediaEquipmentSection.push(
                <MediaSection
                    masterViewModel={this.state}
                    model={this.state.MediaViewModel}
                    handleFormUpdateForMedia={(e) => { this.handleFormUpdateForMedia(e); }}
                    GetMLData={this.GetMLData}
                />
            )
        }

        //Render each of the sections here
        return (
            <ErrorBoundary>
                <CacheProvider value={emotionCache}>
                    <ThemeProvider theme={theme}>
                        {translationEditorToggle}
                        <CookieConsentModal
                            IsInTranslationEditorMode={this.state.IsInTranslationEditorMode}
                            Translations={this.state.Translations}
                            Message={cookieModalProps.Message}
                            RequireInternationalConsent={cookieModalProps.RequireInternationalConsent}
                            CookieSettings={cookieModalProps.CookieSettings}
                            Classifications={cookieModalProps.Classifications}
                            ShowCookieModal={this.state.ShowCookieModal}
                            CurrentUserIsAuthenticated={this.state.IsRegisteredUser}
                            CurrentTrackingUserID={this.state.CurrentTrackingUserID}
                            setShowModalParentState={(showModal) => this.setState({ ShowCookieModal: showModal })}
                        />
                        <div>
                            <div className="free-listing cf" style={{ display: "flex" }}>
                                <div className="main-container">
                                    {this.state.IsRearrangedDetailsPage ? (
                                        <div>
                                            <div className="expand">
                                                <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(ContactSectionID); }} className="expanded PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-3"><strong>{contactOrder} {this.getTranslation("ContactInfo")}</strong></button>
                                            </div>

                                            <ContactSection
                                                masterViewModel={this.state}
                                                ref={this.contactSectionRef}
                                                model={this.state.ContactViewModel}
                                                handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                                                submitListing={(e) => { this.submitListing(e); }}
                                                SaveAllSections={() => { this.SaveAllSections(); }}
                                            />

                                            {mediaEquipmentSection}

                                            {consignmentSection}

                                            <div className="expand">
                                                <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(SubmissionSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-4"><strong>{submissionOrder} {this.getTranslation("AdSubmission")}</strong></button>
                                            </div>
                                            <SubmissionSection
                                                masterViewModel={this.state}
                                                contactViewModel={this.state.ContactViewModel}
                                                handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                                                submitListing={(e) => { this.submitListing(e); }}
                                                saveAllSections={() => { this.SaveAllSections(); }}
                                                contactRef={this.contactSectionRef.current}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            {/*Attachment Radio Selector*/}
                                            {WhatAreYouSelling}
                                            <div>
                                                <div className="expand">
                                                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(MediaSectionID); }} className="expanded PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-1"><strong>1. {this.getTranslation("MediaHeader")}</strong></button>
                                                </div>
                                                <MediaSection
                                                    masterViewModel={this.state}
                                                    model={this.state.MediaViewModel}
                                                    handleFormUpdateForMedia={(e) => { this.handleFormUpdateForMedia(e); }}
                                                    GetMLData={this.GetMLData}
                                                />

                                                <div className="expand">
                                                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(EnterInfoSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-2"><strong>2. {infoHeader}</strong></button>
                                                </div>
                                                {InfoSection}

                                                {logSection}

                                                {consignmentSection}

                                                <div className="expand">
                                                    <button role="link" onClick={(e) => { this.SaveAllSections(); ToggleHeaderAccordion(ContactSectionID); }} className="closed PrimaryBGC siteFontColor preserveDefaultBehaviorWithTranslationEditor" id="header-accordion-3"><strong>{contactOrder} {this.getTranslation("ContactHeader")}</strong></button>
                                                </div>
                                                <ContactSection
                                                    masterViewModel={this.state}
                                                    model={this.state.ContactViewModel}
                                                    handleFormUpdate={(e) => { this.handleFormUpdate(e); }}
                                                    submitListing={(e) => { this.submitListing(e); }}
                                                    SaveAllSections={() => { this.SaveAllSections(); }}
                                                />
                                            </div>
                                        </>
                                    )
                                    }
                                </div>

                                <div className="free-listing cf">
                                    {previewSectionHolder}
                                </div>

                                {/*Recaptcha*/}
                                <div className="g-recaptcha"
                                    data-sitekey={this.state.InvisibleReCAPTCHAPublicKey}
                                    data-callback="verifyReCAPTCHA"
                                    data-size="invisible"
                                    style={{ textAlign: '-webkit-center' }}>
                                </div>

                                {/*Stored info for Recaptcha*/}

                                {/*General*/}
                                <div className="hidden-reCAPTCHA-Type" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-ExcludeGoodsAndServicesTax" style={{ display: 'none' }}></div>

                                {/*Create Account*/}
                                <div className="hidden-reCAPTCHA-Email" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-CRMID" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Username" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-ReceiveEmails" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-FirstName" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-LastName" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Phone" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Company" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-EmailIsRegistered" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Address" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-City" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-State" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-PostalCode" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Country" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-Password" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-QueryString" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-VerifyUserName" style={{ display: 'none' }}></div>
                                <div className="hidden-reCAPTCHA-UnfinishedLeadNewPartialCRMID" style={{ display: 'none' }}></div>

                                {/*Reset Password*/}
                                <div className="hidden-reCAPTCHA-ResetEmail" style={{ display: 'none' }}></div>

                                {/* save Modal*/}
                                <div className="modal" id="save-modal">
                                    <div className="modal-content">
                                        <div className="modal-head">
                                            <h2>{this.getTranslation("Save")}</h2>
                                            <span className="close" onClick={(e) => { CloseModal(); }}><i className="fa fa-times" aria-hidden="true"></i></span>
                                        </div>
                                        <div className="modal-main">
                                            {this.getTranslation("SaveAndExitMessage") + " " + this.getTranslation("SavedFor30Days")}
                                        </div>
                                        <div className="modal-buttons cf">
                                            <button role="link" id="save-modal-save-for-later" className="save-modal" onClick={(e) => { this.SaveAndExit(); }}>{this.getTranslation("SaveForLater")}</button>
                                            <button role="link" className="cancel-modal" onClick={(e) => { CloseModal(); }}>{this.getTranslation("Cancel")}</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="help-footer cf">
                                <div className="help-buttons">
                                    <a href={"tel:1-" + this.state.HelpPhoneNumber}>
                                        <i className="fa fa-phone" aria-hidden="true"></i> &nbsp; {this.state.HelpPhoneNumber}
                                    </a>
                                    <a href={this.state.FeedBackEmailLink} title={this.getTranslation("OpensInANewTab")} target="_blank">
                                        <i className="fa fa-envelope" aria-hidden="true"></i> &nbsp; {this.getTranslation("Contact")} {/*//Make this translation*/}
                                    </a>
                                </div>
                            </div>
                                                           
                        </div>
                    </ThemeProvider>
                </CacheProvider>
            </ErrorBoundary>
        );
    }
}

export default MasterView;
global.MasterView = MasterView;

global.React = React;
global.ReactDOM = ReactDOM;
global.ReactDOMServer = ReactDOMServer;
global.ChunkExtractor = ChunkExtractor
global.loadableReady = loadableReady;
global.createEmotionServer = createEmotionServer;
global.createCache = createCache;